import { z } from "zod";
import { pipeMetadataSchema } from "./pipe.js";
import { contributorSchema } from "./contributor.js";
import { clipRotationAngleLiterals } from "./render-config.js";
// segments uploaded/recording using Pipe will go through a series of events
// while being processed. Since we create the segment on the first event, we
// need to track the status of the segment as it goes through the pipeline.
export const segmentProcessingStatuses = [
    // segment has been uploaded/recording but is still being processed
    "processing",
    // The segment has been processed but is not valid. This will happen for
    // e.g. audio-only videos. Note that prior to adding the processingStatus
    // prop, all segments were assumed to be "complete". So we will still have to
    // e.g. check the thumbUrl (for "audio_only" or "audio_file") and whether
    // videoUrl is set to determine if the segment is valid.
    "invalid",
    // segment is ready to be used
    "complete",
];
const segmentSharedSchema = z.object({
    segmentId: z.string(),
    cardId: z.string(),
    // If processingStatus is not defined, we'll have to infer its state from
    // other values (e.g. if videoUrl is not defined for a video segment, it's
    // still "processing", if snapshotUrl is "audio_file", it's "invalid", etc.).
    // This is necessary be
    processingStatus: z.enum(segmentProcessingStatuses).optional(),
    thumbUrl: z.string().optional(),
    createdAt: z.number().int().nonnegative(),
    sortOrder: z.number(),
    isLocked: z.boolean().optional(),
    // The user is not allowed to edit this segment and it won't be included in a
    // compilation
    isDeleted: z.boolean().optional(),
});
export const segmentVideoProviders = ["pipe", "ziggeo"];
export const segmentEditTextSchema = z.object({
    // These were nullable in an earlier version of the app and
    // some older segments may still have nul values here. It is
    // currently impossible to create more null values here, other
    // than by manually editing the database.
    // TODO: Address this with a normalization layer.
    header: z.string().nullable(),
    subHeader: z.string().nullable().optional(),
});
export const segmentVideoEditSchema = z.object({
    text: segmentEditTextSchema.optional(),
    cutInfo: z
        .object({
        start: z.number(),
        end: z.number(),
        cut: z.boolean(),
    })
        .optional(),
    skipFace: z.boolean().optional(),
    muteMusic: z.boolean().optional(),
    // degrees
    rotateAngle: z.number().optional(),
});
export const photoSegmentSchema = segmentSharedSchema.extend({
    type: z.literal("photo"),
    mimeType: z.string(),
    durationSeconds: z.number(),
    urls: z.array(z.string().url()),
    width: z.number(),
    height: z.number(),
    edit: z.object({
        text: segmentEditTextSchema.optional(),
        // degrees
        rotateAngle: z.union(clipRotationAngleLiterals).optional(),
        skipFace: z.boolean().optional(),
    }),
});
export const videoSegmentSchema = segmentSharedSchema.extend({
    type: z.literal("video").optional(),
    videoUrl: z.string().optional(),
    video_duration: z.string().optional(),
    videoProvider: z.enum(segmentVideoProviders).optional(),
    metadata: pipeMetadataSchema.optional(),
    pipeId: z.string(),
    showOnRecordingPage: z.boolean(),
    contributorId: z.string().optional(),
    edit: segmentVideoEditSchema.optional(),
    submitter: z
        .object({
        email: z.string(),
    })
        .optional(),
    // The username of who recorded this video (if available)
    submitterUsername: z.string().optional(),
    /* Legacy DB fields - no longer active, but may exist in older records */
    chunk: z.record(z.string(), z.any()).optional(), // V1 legacy rendering
    text: segmentEditTextSchema.optional(),
    /* Optionally populated by API */
    contributor: contributorSchema.optional(),
    videoCdnUrl: z.string().optional(),
});
export const segmentSchema = z.discriminatedUnion("type", [
    photoSegmentSchema,
    videoSegmentSchema,
]);
export function isPhotoSegment(segment) {
    return segment.type === "photo";
}
export function isVideoSegment(segment) {
    return !segment.type || segment.type === "video";
}
